/**
 * NPD Opportunity Engine - Coming Soon
 * 
 * Clean, polished design following PIQ standards (ML Command Center style)
 * NO FAKE DATA - Shows proper Coming Soon state
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import GlobalHeader from '../../components/GlobalHeader';
import ExampleQuestions from '@/components/agents/ExampleQuestions';
import { 
  Loader2, 
  Lightbulb,
  TrendingUp,
  Target,
  RefreshCw,
  Beaker,
  Users,
  DollarSign,
  Sparkles,
  BarChart3,
  Rocket,
  CheckCircle2,
  Clock,
  Zap,
  Brain,
  LineChart,
  PieChart,
  FlaskConical,
  ShoppingBag,
  ChevronRight
} from 'lucide-react';

const EXAMPLE_QUESTIONS = [
  "What whitespace opportunities exist for Buttah?",
  "Which ingredients are trending in skincare?",
  "What NPD opportunities should we prioritize?",
  "Evaluate a lip product line opportunity",
  "Which consumer cohorts should we target?"
];

const NPDOpportunity: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dataAvailable, setDataAvailable] = useState(false);

  useEffect(() => {
    checkDataStatus();
  }, []);

  const checkDataStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/npd/opportunities').then(r => r.json());
      setDataAvailable(res.dataStatus !== 'unavailable' && res.data?.length > 0);
    } catch {
      setDataAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  // Connected agents
  const connectedAgents = [
    { title: 'Competitor Intel', href: '/agents/competitor-intel', icon: BarChart3, gradient: 'from-red-500 to-rose-600' },
    { title: 'Creative Intel', href: '/agents/creative-intel', icon: Sparkles, gradient: 'from-purple-500 to-violet-600' },
    { title: 'Retail Story', href: '/agents/retail-story', icon: ShoppingBag, gradient: 'from-blue-500 to-cyan-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader 
        title="NPD Opportunity Engine"
        subtitle="Category whitespace, ingredient trends & new product opportunities"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Status Banner */}
        <div className="rounded-2xl shadow-lg border-2 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-amber-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg">
                  <Lightbulb className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">NPD Intelligence</h2>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-sm px-3 py-1">
                      Coming Soon
                    </Badge>
                  </div>
                  <p className="text-gray-600 max-w-xl">
                    AI-powered product opportunity analysis will identify whitespace, trending ingredients, 
                    and high-potential NPD opportunities from your actual sales data.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={checkDataStatus} 
                disabled={loading}
                className="bg-white hover:bg-gray-50 shadow-sm"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Check Status
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Whitespace */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-blue-700">Whitespace Opportunities</span>
              <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                <Target className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-blue-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting activation</span>
            </div>
          </div>

          {/* Trending */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-emerald-700">Trending Ingredients</span>
              <div className="p-3 bg-emerald-500 rounded-xl shadow-md">
                <Beaker className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-emerald-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting activation</span>
            </div>
          </div>

          {/* Opportunities */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-amber-700">NPD Opportunities</span>
              <div className="p-3 bg-amber-500 rounded-xl shadow-md">
                <Lightbulb className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-amber-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-amber-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting activation</span>
            </div>
          </div>

          {/* Revenue */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-purple-700">Est. Year 1 Revenue</span>
              <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-purple-700 mb-2">—</div>
            <div className="flex items-center gap-1 text-sm text-purple-600">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Awaiting activation</span>
            </div>
          </div>
        </div>

        {/* Main Coming Soon Card */}
        <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">NPD Intelligence Hub</h3>
                <p className="text-slate-300">Powered by your sales data • No fake estimates</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-8">
            {/* Capabilities Grid */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Rocket className="h-5 w-5 text-amber-500" />
                What you'll get when activated
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Whitespace */}
                <div className="p-5 rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-blue-100">
                      <Target className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Category Whitespace</h5>
                      <p className="text-sm text-gray-600">AI identifies price gaps and underserved market segments</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Ingredients */}
                <div className="p-5 rounded-xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-emerald-100">
                      <FlaskConical className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Ingredient Trends</h5>
                      <p className="text-sm text-gray-600">Track rising ingredients based on sales velocity</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Opportunities */}
                <div className="p-5 rounded-xl border-2 border-amber-100 bg-gradient-to-br from-amber-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-amber-100">
                      <Lightbulb className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">NPD Opportunities</h5>
                      <p className="text-sm text-gray-600">Prioritized product concepts with revenue projections</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>

                {/* Cohorts */}
                <div className="p-5 rounded-xl border-2 border-purple-100 bg-gradient-to-br from-purple-50/50 to-white hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 rounded-xl bg-purple-100">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Consumer Cohorts</h5>
                      <p className="text-sm text-gray-600">Segment affinities from purchase patterns</p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-6 border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                How NPD Intelligence Works
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    1
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Analyze Sales Data</h5>
                    <p className="text-sm text-gray-600">AI scans your SKU performance, velocity, and trends</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    2
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Identify Gaps</h5>
                    <p className="text-sm text-gray-600">Find whitespace in categories, price points, and ingredients</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                    3
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Prioritize Opportunities</h5>
                    <p className="text-sm text-gray-600">Score and rank NPD concepts by potential and fit</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Questions */}
        <ExampleQuestions 
          questions={EXAMPLE_QUESTIONS}
          accentColor="yellow"
          title="Ask the NPD Opportunity Agent"
          description="Click any question to explore new product opportunities"
        />

        {/* Connected Agents */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Connected Agents</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {connectedAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <Link key={agent.href} href={agent.href}>
                  <div className="group bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:border-amber-300 transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${agent.gradient} shadow-md`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-semibold text-gray-900">{agent.title}</span>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-amber-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Roadmap Preview */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">NPD Intelligence Roadmap</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 shadow-sm">
                  <LineChart className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Sales-Derived Insights</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Category gaps from revenue data</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Trend Detection</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Ingredient velocity tracking</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-sm">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Revenue Projections</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">NPD estimates from similar SKUs</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-sm">
                  <PieChart className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Market Analysis</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">Competitive positioning</p>
              <Badge variant="outline" className="text-xs text-gray-500">Coming Soon</Badge>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default NPDOpportunity;
